/**
 * jp-tooltip.ts — galaxy-view jump-preview tooltip.
 *
 * v4M5.3b3e — reads UiState.hoveredGalaxySystemId (set by input.ts on
 * mouse-move in galaxy view) and, if non-null, shows a small DOM
 * panel near the cursor with destination system name + per-fleet fuel
 * cost for the currently-selected ships + a range-cap warning when
 * applicable. Pure reader; never mutates state.
 */

import type { GameState } from '../state/types';
import type { UiState } from './state';
import { fuelCostForJump } from '../game/commands';
import { hyperdriveRangeMultiplier } from '../game/research-effects';

export interface JpTooltipHandle {
  update(state: GameState, ui: UiState): void;
  destroy(): void;
}

export function mountJpTooltip(parent: HTMLElement): JpTooltipHandle {
  const root = document.createElement('div');
  root.id = 'jp-tooltip';
  Object.assign(root.style, {
    position: 'fixed',
    left: '0px',
    top: '0px',
    padding: '6px 8px',
    background: 'rgba(10, 14, 24, 0.92)',
    border: '1px solid rgba(120, 160, 230, 0.4)',
    borderRadius: '3px',
    color: '#d8e4ff',
    font: '11px / 1.35 monospace',
    pointerEvents: 'none',
    zIndex: '20',
    display: 'none',
    whiteSpace: 'nowrap',
  } as Partial<CSSStyleDeclaration>);
  parent.appendChild(root);

  return {
    update(state: GameState, ui: UiState): void {
      const hoveredId = ui.hoveredGalaxySystemId;
      const anchor = ui.hoveredGalaxyAnchor;
      if (
        hoveredId === null ||
        !anchor ||
        ui.viewMode !== 'galaxy' ||
        ui.selectedShipIds.size === 0
      ) {
        root.style.display = 'none';
        return;
      }
      const destSys = state.systems.get(hoveredId);
      if (!destSys) { root.style.display = 'none'; return; }
      const empire = state.empires.get(state.playerEmpireId);
      if (!empire) { root.style.display = 'none'; return; }

      let sourceSystemId: number | null = null;
      const shipIds: number[] = [];
      let fleetRange = Number.POSITIVE_INFINITY;
      const rangeMul = hyperdriveRangeMultiplier(state, empire);
      for (const id of ui.selectedShipIds) {
        const ship = state.ships.get(id);
        if (!ship) continue;
        if (ship.ownerEmpireId !== state.playerEmpireId) continue;
        if (ship.systemId === null) continue;
        if (sourceSystemId === null) sourceSystemId = ship.systemId;
        else if (sourceSystemId !== ship.systemId) {
          root.style.display = 'none';
          return;
        }
        shipIds.push(id);
        const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
        if (tmpl) {
          const r = tmpl.hyperdriveRange * rangeMul;
          if (r < fleetRange) fleetRange = r;
        }
      }
      if (sourceSystemId === null || shipIds.length === 0) {
        root.style.display = 'none';
        return;
      }
      if (hoveredId === sourceSystemId) {
        root.style.display = 'none';
        return;
      }
      const srcSys = state.systems.get(sourceSystemId);
      if (!srcSys) { root.style.display = 'none'; return; }

      const pathDistance = Math.hypot(srcSys.x - destSys.x, srcSys.y - destSys.y);
      const outOfRange = pathDistance > fleetRange;
      const fuel = fuelCostForJump(state, shipIds, pathDistance);
      const have = empire.resources.fuel;
      const affordable = have >= fuel;
      const scouted = empire.scoutedSystemIds.has(destSys.id);
      const nameLabel = scouted ? destSys.name : '?';
      const costColour = !affordable || outOfRange ? '#ff8f8f' : '#8fffb0';
      const distLabel = pathDistance.toFixed(2);
      const rangeLabel = Number.isFinite(fleetRange) ? fleetRange.toFixed(2) : '∞';

      root.innerHTML =
        `<div style="color:#cfe0ff"><strong>Jump to ${escapeHtml(nameLabel)}</strong></div>` +
        `<div>ships: ${shipIds.length}</div>` +
        `<div>fuel: <span style="color:${costColour}">${fuel.toFixed(1)}</span> / ${have.toFixed(1)}</div>` +
        `<div>distance: ${distLabel} / range ${rangeLabel}</div>` +
        (outOfRange ? `<div style="color:#ff8f8f">out of fleet range</div>` : '');

      root.style.left = `${anchor.x + 12}px`;
      root.style.top = `${anchor.y + 12}px`;
      root.style.display = 'block';
    },
    destroy(): void {
      root.remove();
    },
  };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
